import { Component, computed, inject, Injector, signal } from '@angular/core';
import {
  LucideChevronDown,
  LucideLogOut,
  LucideMenu,
  LucideSettings,
  LucideUser,
} from '@lucide/angular';

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { NgClass, NgTemplateOutlet } from '@angular/common';

import {

  ActivatedRoute,

  NavigationEnd,

  Router,

  RouterLink,

  RouterOutlet,

} from '@angular/router';

import { filter, take } from 'rxjs';



import { NzLayoutModule } from 'ng-zorro-antd/layout';

import { NzAvatarModule } from 'ng-zorro-antd/avatar';

import { NzDropDownModule } from 'ng-zorro-antd/dropdown';

import { NzMenuModule } from 'ng-zorro-antd/menu';

import { NzButtonModule } from 'ng-zorro-antd/button';

import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';

import { NzModalModule } from 'ng-zorro-antd/modal';



import { SideMenu } from './core/layout/side-menu/side-menu';

import { PageActionsService } from './core/layout/page-actions/page-actions.service';

import { BreakpointService } from './core/layout/breakpoint.service';

import { AuthService } from './core/auth/auth.service';

import { ProfilePhotoService } from './core/auth/profile-photo.service';

import { SessionMonitorService } from './core/auth/session-monitor.service';

import { SubscriptionGateService } from './core/auth/subscription-gate.service';

import { ConnectivityService } from './core/sync/connectivity.service';
import { PageContextService } from './core/layout/page-context.service';
import { AppUpdateService } from './core/pwa/app-update.service';
import { I18nService } from './core/i18n/i18n.service';
import { I18nTitleService } from './core/i18n/i18n-title.service';
import { LocaleService } from './core/i18n/locale.service';
import { TranslatePipe } from './core/i18n/translate.pipe';
import { ExportProgressOverlay } from './shared/export/components/export-progress-overlay/export-progress-overlay';



@Component({

  selector: 'app-root',

  imports: [

    RouterLink,

    RouterOutlet,

    NgClass,

    NgTemplateOutlet,

    SideMenu,

    NzLayoutModule,

    NzAvatarModule,

    NzDropDownModule,

    NzMenuModule,

    NzButtonModule,

    NzTooltipModule,

    NzBreadCrumbModule,

    NzModalModule,
    LucideMenu,
    LucideChevronDown,
    LucideSettings,
    LucideUser,
    LucideLogOut,
    TranslatePipe,
    ExportProgressOverlay,

  ],

  templateUrl: './app.html',

  styleUrl: './app.scss',

})

export class App {
  private requestedStoragePersistence = false;

  private readonly router = inject(Router);

  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly auth = inject(AuthService);
  private readonly injector = inject(Injector);
  private readonly i18n = inject(I18nService);
  private readonly locale = inject(LocaleService);

  private readonly profilePhoto = inject(ProfilePhotoService);

  /** Keeps session validation running after login (not only on the login route). */
  private readonly _sessionMonitor = inject(SessionMonitorService);



  protected readonly pageActions = inject(PageActionsService);

  protected readonly bp = inject(BreakpointService);

  protected readonly gate = inject(SubscriptionGateService);

  private readonly appUpdate = inject(AppUpdateService);



  protected readonly appName = signal('NutriApp');

  protected readonly siderCollapsed = signal(false);



  protected readonly userName = computed(() => {
    this.locale.locale();
    return this.auth.user()?.name ?? this.i18n.t('sync.guest');
  });
  protected readonly isAdmin = computed(() => this.auth.isAdmin());
  protected readonly homeLink = computed(() => (this.auth.isAdmin() ? '/admin/system/foods' : '/home'));

  protected readonly userAvatarUrl = this.profilePhoto.photoUrl;



  protected readonly connectivityLabel = computed(() => {
    this.locale.locale();
    return this.isOnline() ? this.i18n.t('sync.online') : this.i18n.t('sync.offline');
  });

  protected readonly connectivityHint = computed(() => {
    this.locale.locale();
    return this.isOnline() ? this.i18n.t('sync.onlineHint') : this.i18n.t('sync.offlineHint');
  });

  protected readonly connectivityChipClass = computed(() =>
    this.isOnline() ? 'sync-toolbar__status--online' : 'sync-toolbar__status--disconnected',
  );



  protected readonly layoutMode = signal<'shell' | 'blank' | 'patient'>('shell');



  private readonly pageContext = inject(PageContextService);



  protected readonly pageTitle = computed(() => {
    this.locale.locale();
    this._navigationTick();
    return this.pageContext.titleOverride() ?? this.computePageTitle();
  });

  private readonly _navigationTick = signal(0);

  protected readonly breadcrumbItems = computed(() => {
    this.locale.locale();
    this._navigationTick();
    return this.computeBreadcrumbs();
  });



  constructor() {
    inject(I18nTitleService).init();
    this.appUpdate.start();

    void this.profilePhoto.ensureLoaded();

    toObservable(this.bp.isDesktop)
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => this.siderCollapsed.set(!isDesktop));

    this.router.events

      .pipe(

        filter((e): e is NavigationEnd => e instanceof NavigationEnd),

        takeUntilDestroyed(),

      )

      .subscribe(() => {

        this.pageContext.clear();

        this._navigationTick.update((n) => n + 1);

        this.layoutMode.set(this.computeLayoutMode());

      });

    toObservable(computed(() => this.auth.isAuthenticated() && this.auth.isNutritionist()))
      .pipe(
        filter((ready) => ready),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (!this.requestedStoragePersistence) {
          this.requestedStoragePersistence = true;
          void this.requestPersistentStorage();
        }
      });

  }



  toggleSider(): void {

    this.siderCollapsed.update((c) => !c);

  }



  logout(): void {

    this.profilePhoto.clear();

    void this.auth.logout();

  }

  protected navigateFromUserMenu(path: string): void {
    void this.router.navigate([path]);
  }



  dismissGate(): void {

    this.gate.dismiss();

  }

  isOnline(): boolean {
    return this.getConnectivity()?.isOnline() ?? false;
  }



  openBillingPortal(): void {
    const url = this.gate.blocked()?.portal_url;
    if (!url) return;
    window.open(url, '_blank', 'noopener');
    this.dismissGate();
  }



  private computePageTitle(): string {
    let route: ActivatedRoute | null = this.activatedRoute.root;
    let titleKey = '';
    let breadcrumb = '';

    while (route) {
      const data = route.snapshot.data;
      const key = (data['titleKey'] ?? data['breadcrumbKey']) as string | undefined;
      if (key) titleKey = key;
      const label = data['breadcrumb'];
      if (typeof label === 'string' && label) breadcrumb = label;
      route = route.firstChild;
    }

    if (titleKey) return this.i18n.t(titleKey);
    return breadcrumb;
  }

  private computeBreadcrumbs(): { label: string; url: string }[] {
    const items: { label: string; url: string }[] = [];
    let route: ActivatedRoute | null = this.activatedRoute.root;
    let url = '';

    while (route?.firstChild) {
      route = route.firstChild;
      const segmentPaths = route.snapshot.url.map((s) => s.path);
      if (segmentPaths.length > 0) {
        url += `/${segmentPaths.join('/')}`;
      }

      const data = route.snapshot.data;
      const key = data['breadcrumbKey'] as string | undefined;
      const label = key
        ? this.i18n.t(key)
        : typeof data['breadcrumb'] === 'string'
          ? data['breadcrumb']
          : null;
      const itemUrl = url || '/';
      // Rutas hijas con path vacío (index routes) pueden repetir la misma url
      // que su padre; evita crumbs duplicados (y colisiones en el track del @for).
      if (label && items[items.length - 1]?.url !== itemUrl) {
        items.push({ label, url: itemUrl });
      }
    }

    return items;
  }



  private computeLayoutMode(): 'shell' | 'blank' | 'patient' {

    let route: ActivatedRoute | null = this.activatedRoute.root;

    let mode: 'shell' | 'blank' | 'patient' = 'shell';

    while (route) {

      const layout = route.snapshot.data['layout'];

      if (layout === 'blank') mode = 'blank';

      if (layout === 'patient') mode = 'patient';

      route = route.firstChild;

    }

    return mode;

  }

  private getConnectivity(): ConnectivityService | null {
    if (!this.auth.isAuthenticated() || !this.auth.isNutritionist()) return null;
    return this.injector.get(ConnectivityService);
  }

  private async requestPersistentStorage(): Promise<void> {
    if (!('storage' in navigator) || !navigator.storage?.persist) return;
    try {
      await navigator.storage.persist();
    } catch {
      // Best effort: if denied, app still works with remote source of truth.
    }
  }

}

